import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, ShieldCheck, Download, Linkedin, Twitter, Link2, Check } from "lucide-react";
import { linkedInAddUrl, twitterShareUrl, copyVerifyLink, openShare, pdfUrl } from "@/lib/share";

const LEVEL_ACCENT = {
  beginner: "#00E5FF",
  intermediate: "#FFB020",
  advanced: "#FF3B30",
  developer: "#818CF8",
  supplychain: "#84CC16",
  infra: "#F97316",
  claudecode101: "#0EA5E9",
};

const LEVEL_LABEL = {
  beginner: "AI Security Analyst",
  intermediate: "AI Red Teamer",
  advanced: "AI Red Team Operator",
  developer: "AI Application Security Engineer",
  supplychain: "AI Supply Chain Security",
  infra: "AI Infrastructure Security",
  claudecode101: "Claude Code 101",
};

export default function Verify() {
  const { verificationId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/verify/${verificationId}`).then((r) => setResult(r.data)).finally(() => setLoading(false));
  }, [verificationId]);

  if (loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center font-mono text-sm text-zinc-500">Verifying credential…</div>;
  }

  if (!result?.valid) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="max-w-md w-full border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-10 text-center" data-testid="verify-invalid">
          <XCircle className="w-14 h-14 text-[#FF3B30] mx-auto mb-4" />
          <h1 className="text-3xl font-display font-bold tracking-tight text-white">Not found</h1>
          <p className="text-zinc-400 mt-2 text-sm">No ILFAL certificate matches</p>
          <p className="font-mono text-xs text-zinc-500 mt-1" data-testid="verify-id">{verificationId}</p>
        </div>
      </div>
    );
  }

  const cert = { ...result, verification_id: verificationId };
  const accent = LEVEL_ACCENT[result.level] || "#00E5FF";
  const issued = new Date(result.issued_at);

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden" data-testid="verify-valid">
      {/* grid texture */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)",
        backgroundSize: "26px 26px",
      }} />
      {/* accent glow */}
      <div className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full blur-3xl opacity-20" style={{ background: accent }} />

      <div className="relative max-w-3xl mx-auto px-6 py-14">
        {/* verified status pill */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400">Verified credential</span>
        </div>

        {/* the framed certificate */}
        <div className="border border-white/15 bg-[#0A0A0A]" style={{ boxShadow: `0 0 0 1px ${accent}22, 0 40px 120px -40px ${accent}33` }}>
          <div className="h-1" style={{ background: accent }} />
          <div className="p-6 sm:p-12">
            {/* header */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-10">
              <div className="flex items-center gap-2">
                <span className="grid place-items-center w-7 h-7 bg-[#FF3B30] font-mono font-bold text-sm">{">_"}</span>
                <span className="font-display font-bold text-lg tracking-tight">ILFAL</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">AI Security Credential</span>
            </div>

            <div className="font-mono text-[11px] uppercase tracking-[0.35em] mb-3" style={{ color: accent }}>
              Certificate of Completion
            </div>
            <p className="text-sm text-zinc-500 italic mb-4">This credential is proudly presented to</p>

            <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl leading-none" data-testid="verify-holder">
              {result.user_name}
            </h1>
            <div className="w-24 h-[3px] mt-4 mb-6" style={{ background: "#FF3B30" }} />

            <p className="text-zinc-400 text-base max-w-xl">
              has successfully completed the <span className="text-white font-semibold">{LEVEL_LABEL[result.level] || result.level}</span> track
              of the ILFAL AI Security Curriculum — demonstrating hands-on mastery of attacking and defending modern AI systems.
            </p>

            {/* meta row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10 pt-6 border-t border-white/10">
              <Meta label="Track" value={LEVEL_LABEL[result.level] || result.level} accent={accent} mono={false} />
              <Meta label="Issued" value={issued.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })} />
              <Meta label="Verification ID" value={verificationId} accent={accent} small />
            </div>
          </div>
        </div>

        {/* actions */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-testid="verify-actions">
          <a href={pdfUrl(verificationId)} target="_blank" rel="noreferrer">
            <Button data-testid="verify-download" className="rounded-none bg-white text-black hover:bg-zinc-200 font-mono text-xs uppercase tracking-wider">
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </a>
          <Button onClick={() => openShare(linkedInAddUrl(cert))} data-testid="verify-share-linkedin"
            className="rounded-none bg-[#0A66C2] hover:bg-[#0A66C2]/90 text-white font-mono text-xs uppercase tracking-wider">
            <Linkedin className="w-4 h-4 mr-2" /> Add to LinkedIn
          </Button>
          <Button onClick={() => openShare(twitterShareUrl(cert))} data-testid="verify-share-x"
            className="rounded-none bg-transparent border border-white/20 text-white hover:bg-white hover:text-black font-mono text-xs uppercase tracking-wider">
            <Twitter className="w-4 h-4 mr-2" /> Share on X
          </Button>
          <Button onClick={() => copyVerifyLink(verificationId)} data-testid="verify-share-copy"
            className="rounded-none bg-transparent border border-white/20 text-white hover:bg-white hover:text-black font-mono text-xs uppercase tracking-wider">
            <Link2 className="w-4 h-4 mr-2" /> Copy link
          </Button>
        </div>

        <div className="mt-10 flex items-center justify-center gap-2 text-zinc-600">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span className="font-mono text-[11px]">Authenticated by ILFAL · ilfal.com</span>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, accent, small, mono = true }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 flex items-center gap-1">
        <Check className="w-3 h-3" style={{ color: accent }} /> {label}
      </div>
      <div className={`${mono ? "font-mono" : "font-display font-bold"} ${small ? "text-xs break-all" : "text-sm"}`}
        style={accent ? { color: accent } : undefined}>
        {value}
      </div>
    </div>
  );
}
